import { z } from "zod";
import { nlWkrCategorySchema } from "./nlWkr.js";
/**
 * Schema for POST /api/v1/ledger-transactions/orgs/:orgId/journal-entries.
 *
 * Wraps the `create_journal_transaction` SECURITY INVOKER RPC (migration
 * 20260706120000). Replaces the direct `supabase.from('ledger_transactions'
 * / 'ledger_entries').insert(...)` writes the manual-JE hook used to do
 * (HIGH-1 / SCRUM-181). The org id lives in the URL path; the body is the
 * journal entry. Balance is validated authoritatively in the RPC.
 */
const journalEntryLineSchema = z
    .object({
    account_id: z.string().uuid(),
    fund_id: z.string().uuid().nullable().optional(),
    debit_amount: z.number().nonnegative().optional(),
    credit_amount: z.number().nonnegative().optional(),
    description: z.string().nullable().optional(),
    /** NL WKR classification (jurisdiction-gated in the UI; rides through to nl_wkr_sync_from_lines). */
    wkr_category: nlWkrCategorySchema.optional(),
})
    .strict();
export const createJournalTransactionSchema = z
    .object({
    // `ledger_transactions.transaction_date` is a plain `date` column and the
    // RPC param is `date`, so the JE modal now sends a bare `yyyy-MM-dd`.
    //
    // TRANSITION (one release): the old modal's full `toISOString()` wire shape
    // is still accepted so a web tab left open across the deploy keeps working
    // — Postgres takes the literal date part of `'2026-03-15T23:30:00.000Z'`
    // when casting to `date`. Documented residual for that one release: a stale
    // tab in a UTC+ zone posting between local midnight and the UTC offset
    // sends the PREVIOUS UTC day, which is now stored literally (there is no
    // org-timezone read layer left to mask it) — accepted, bounded by the
    // release, and only reachable from a tab that predates the deploy.
    //
    // TODO(+1 release): tighten to date-only (`/^\d{4}-\d{2}-\d{2}$/`) together
    // with the same transition regex on `expenses.ts` (`expense_date`).
    transaction_date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})?)?$/, {
        message: "transaction_date must be an ISO date or datetime",
    }),
    description: z.string().min(1),
    // ledger_transactions has CHECK (total_amount > 0).
    total_amount: z.number().positive(),
    reference: z.string().nullable().optional(),
    project_id: z.string().uuid().nullable().optional(),
    business_id: z.string().uuid().nullable().optional(),
    fund_id: z.string().uuid().nullable().optional(),
    purchase_order_id: z.string().uuid().nullable().optional(),
    invoice_number: z.string().nullable().optional(),
    // A balanced journal entry needs at least two lines; cap the upper bound as
    // defense-in-depth against a pathological single-request payload.
    entries: z.array(journalEntryLineSchema).min(2).max(1000),
})
    .strict();
/**
 * Schema for PATCH /api/v1/ledger-transactions/orgs/:orgId/journal-entries/:transactionId/header.
 *
 * Header-only save for a DECLARED manual journal — wraps
 * `update_ledger_transaction_header`. The full update RPC deletes + re-posts
 * every entry, which the declared-VAT guard refuses once the journal's VAT
 * lines sit on a filed return. This body is the create schema minus everything
 * frozen on a declared journal: `transaction_date`, `total_amount` and
 * `entries`. `.strict()` rejects those rather than silently dropping them.
 */
export const updateJournalHeaderSchema = z
    .object({
    description: z.string().min(1),
    reference: z.string().nullable().optional(),
    project_id: z.string().uuid().nullable().optional(),
    business_id: z.string().uuid().nullable().optional(),
    fund_id: z.string().uuid().nullable().optional(),
    purchase_order_id: z.string().uuid().nullable().optional(),
    invoice_number: z.string().nullable().optional(),
})
    .strict();
/**
 * Schema for PATCH /api/v1/ledger-transactions/orgs/:orgId/entries/:entryId/account.
 * Recategorize a single ledger entry (e.g. from the suspense/clearing workflow).
 * The validate_ledger_entry_balance trigger re-checks the transaction still balances.
 */
export const updateLedgerEntryAccountSchema = z
    .object({
    account_id: z.string().uuid(),
})
    .strict();
/**
 * Schema for POST /api/v1/ledger-transactions/orgs/:orgId/currency-transfers.
 * Wraps create_currency_transfer (writes an FX-clearing ledger transaction).
 */
export const createCurrencyTransferSchema = z
    .object({
    from_account_id: z.string().uuid(),
    to_account_id: z.string().uuid(),
    amount: z.number().positive(),
    exchange_rate: z.number().positive(),
    // The hook sends toISOString().split('T')[0] → YYYY-MM-DD (RPC param is date).
    transaction_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, {
        message: "transaction_date must be YYYY-MM-DD",
    }),
    description: z.string().nullable().optional(),
    reference: z.string().nullable().optional(),
})
    .strict();
// ---------- bulk delete (the journal grids' bulk actions) ----------
/**
 * Max transaction ids per
 * POST /api/v1/ledger-transactions/orgs/:orgId/journal-entries/batch-delete.
 * The web queues the selection and sends it in requests of at most this many
 * (the `EXPENSE_BATCH_MAX_IDS` / `CLEARING_BATCH_MAX_OPS` precedent).
 */
export const JOURNAL_BATCH_MAX_IDS = 50;
/**
 * Request body for the journal batch delete. Each id runs the same
 * `delete_ledger_transaction_with_entries` RPC as the single DELETE route, in
 * input order; every rule (expense / invoice / payment / void / opening
 * balance / bank-linked refusals, declared VAT) lives in that RPC. Duplicates
 * are refused (a second delete of the same row would only report not_found).
 */
export const journalBatchDeleteSchema = z
    .object({
    transaction_ids: z
        .array(z.string().uuid())
        .min(1)
        .max(JOURNAL_BATCH_MAX_IDS)
        .refine((ids) => new Set(ids).size === ids.length, {
        message: "transaction_ids must be unique",
    }),
})
    .strict();
//# sourceMappingURL=ledgerTransactions.js.map